import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/common/LoadingSpinner';

function Section({ title, description, children }) {
  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div style={{ marginBottom: '1.25rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.25rem' }}>{title}</h2>
        {description && <p style={{ fontSize: '0.8rem', color: 'var(--text-3)' }}>{description}</p>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ label, description, checked, onChange }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>
      <div>
        <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{label}</div>
        {description && <div style={{ fontSize: '0.75rem', color: 'var(--text-3)', marginTop: 2 }}>{description}</div>}
      </div>
      <button
        onClick={() => onChange(!checked)}
        style={{
          width: 44, height: 24, borderRadius: 99, border: 'none', cursor: 'pointer',
          background: checked ? '#1596E0' : 'rgba(255,255,255,0.12)',
          position: 'relative', transition: 'background .2s', flexShrink: 0,
        }}
      >
        <span style={{
          position: 'absolute', top: 3, left: checked ? 22 : 3, width: 18, height: 18,
          borderRadius: '50%', background: '#fff', transition: 'left .2s',
          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
        }} />
      </button>
    </div>
  );
}

export default function Settings() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '', confirm_new_password: '' });
  const [passwordMsg, setPasswordMsg] = useState('');
  const [passwordErr, setPasswordErr] = useState('');
  const [deactivatePass, setDeactivatePass] = useState('');
  const [deactivateErr, setDeactivateErr] = useState('');
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtp, setShowOtp] = useState(false);

  const { data: profileData, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: () => authService.getProfile().then(r => r.data.data),
  });

  const notifMutation = useMutation({
    mutationFn: (prefs) => authService.updateNotificationPreferences(prefs),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });

  const passwordMutation = useMutation({
    mutationFn: (d) => authService.changePassword(d),
    onSuccess: () => {
      setPasswordMsg('Password changed successfully.');
      setPasswordErr('');
      setPasswordForm({ old_password: '', new_password: '', confirm_new_password: '' });
    },
    onError: (e) => { setPasswordErr(e.response?.data?.message || 'Password change failed.'); setPasswordMsg(''); },
  });

  const resendMutation = useMutation({
    mutationFn: () => authService.resendOTP('email'),
    onSuccess: () => { setShowOtp(true); setVerifyMsg('OTP sent to your email.'); },
  });

  const verifyMutation = useMutation({
    mutationFn: () => authService.verifyEmail(otp),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['profile'] }); setVerifyMsg('Email verified!'); setShowOtp(false); setOtp(''); },
    onError: () => setVerifyMsg('Invalid OTP. Try again.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: () => authService.deactivateAccount(deactivatePass),
    onSuccess: () => { logout(); navigate('/login'); },
    onError: (e) => setDeactivateErr(e.response?.data?.message || 'Deactivation failed.'),
  });

  if (isLoading) return <LoadingSpinner />;
  const profile = profileData || {};

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_new_password) {
      setPasswordErr('Passwords do not match.');
      return;
    }
    passwordMutation.mutate(passwordForm);
  };

  const handleNotifToggle = (field, val) => {
    notifMutation.mutate({ [field]: val });
  };

  return (
    <div>
      <div className="page-header">
        <h1>Settings</h1>
        <p>Manage your account security and notification preferences</p>
      </div>

      {/* Email Verification */}
      <Section
        title="Email Verification"
        description="A verified email address secures your account and ensures you receive important alerts."
      >
        {profile.is_email_verified ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', background: 'rgba(0,200,150,0.08)', border: '1px solid rgba(0,200,150,0.2)', borderRadius: 'var(--radius)' }}>
            <span style={{ fontSize: '1.25rem' }}>✓</span>
            <span style={{ fontSize: '0.875rem', color: '#00C896', fontWeight: 600 }}>Email verified — {profile.email}</span>
          </div>
        ) : (
          <>
            <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
              <span>✉</span>
              <span style={{ fontSize: '0.875rem' }}>Your email <strong>{profile.email}</strong> is not yet verified.</span>
            </div>
            {verifyMsg && (
              <div className={`alert ${verifyMsg.includes('verified!') ? 'alert-success' : 'alert-info'}`} style={{ marginBottom: '0.75rem' }}>
                <span>{verifyMsg.includes('!') ? '✓' : 'ℹ'}</span><span>{verifyMsg}</span>
              </div>
            )}
            {showOtp ? (
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ margin: 0, flex: 1 }}>
                  <label className="form-label">Enter OTP</label>
                  <input className="form-input" value={otp} onChange={e => setOtp(e.target.value)} maxLength={6} placeholder="6-digit code" />
                </div>
                <button className="btn btn-primary" disabled={!otp || verifyMutation.isPending} onClick={() => verifyMutation.mutate()}>
                  Verify
                </button>
                <button className="btn btn-ghost" onClick={() => { setShowOtp(false); setOtp(''); }}>Cancel</button>
              </div>
            ) : (
              <button className="btn btn-primary" disabled={resendMutation.isPending} onClick={() => resendMutation.mutate()}>
                {resendMutation.isPending ? 'Sending…' : 'Send Verification Email'}
              </button>
            )}
          </>
        )}
      </Section>

      {/* Change Password */}
      <Section title="Change Password" description="Use a strong password with at least 8 characters, numbers, and symbols.">
        {passwordMsg && <div className="alert alert-success" style={{ marginBottom: '1rem' }}><span>✓</span><span>{passwordMsg}</span></div>}
        {passwordErr && <div className="alert alert-error" style={{ marginBottom: '1rem' }}><span>⚠</span><span>{passwordErr}</span></div>}
        <form onSubmit={handlePasswordSubmit}>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input className="form-input" type="password" value={passwordForm.old_password}
              onChange={e => setPasswordForm({ ...passwordForm, old_password: e.target.value })} />
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input className="form-input" type="password" value={passwordForm.new_password}
                onChange={e => setPasswordForm({ ...passwordForm, new_password: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input className="form-input" type="password" value={passwordForm.confirm_new_password}
                onChange={e => setPasswordForm({ ...passwordForm, confirm_new_password: e.target.value })} />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={passwordMutation.isPending || !passwordForm.old_password || !passwordForm.new_password}>
            {passwordMutation.isPending ? 'Changing…' : 'Change Password'}
          </button>
        </form>
      </Section>

      {/* Notification Preferences */}
      <Section title="Notification Preferences" description="Choose how you want to receive alerts and updates from PCredit.">
        <Toggle
          label="Email Notifications"
          description="Loan status updates, payment reminders, and offers"
          checked={profile.notif_email ?? true}
          onChange={v => handleNotifToggle('notif_email', v)}
        />
        <Toggle
          label="SMS Notifications"
          description="OTP codes and critical payment alerts"
          checked={profile.notif_sms ?? true}
          onChange={v => handleNotifToggle('notif_sms', v)}
        />
        <Toggle
          label="Push Notifications"
          description="Real-time alerts on the mobile app"
          checked={profile.notif_push ?? true}
          onChange={v => handleNotifToggle('notif_push', v)}
        />
      </Section>

      {/* Danger Zone */}
      <Section title="Danger Zone" description="Irreversible actions that affect your account.">
        {!showDeactivate ? (
          <button className="btn btn-ghost" style={{ color: '#FF4757', borderColor: '#FF475740' }} onClick={() => setShowDeactivate(true)}>
            Deactivate Account
          </button>
        ) : (
          <div style={{ background: 'rgba(255,71,87,0.06)', border: '1px solid rgba(255,71,87,0.2)', borderRadius: 'var(--radius)', padding: '1rem' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '0.75rem' }}>
              This will deactivate your account immediately. All active loans and repayment obligations remain. Contact support to reactivate.
            </p>
            {deactivateErr && <div className="alert alert-error" style={{ marginBottom: '0.75rem' }}><span>⚠</span><span>{deactivateErr}</span></div>}
            <div className="form-group">
              <label className="form-label">Enter your password to confirm</label>
              <input className="form-input" type="password" value={deactivatePass} onChange={e => setDeactivatePass(e.target.value)} placeholder="Your current password" />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button className="btn btn-ghost" style={{ color: '#FF4757', borderColor: '#FF475740' }}
                disabled={!deactivatePass || deactivateMutation.isPending}
                onClick={() => deactivateMutation.mutate()}>
                {deactivateMutation.isPending ? 'Deactivating…' : 'Confirm Deactivation'}
              </button>
              <button className="btn btn-ghost" onClick={() => { setShowDeactivate(false); setDeactivatePass(''); setDeactivateErr(''); }}>Cancel</button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
